import { DynamicFieldKey_Enum } from '@weave/schema-gen-ts/dist/schemas/messaging/templator/v2/model.pb';
import { useTranslation } from '@frontend/i18n';
import { formatDynamicFieldKey } from '@frontend/string';
import { DynamicFieldAttributes } from '@frontend/design-system';
import { DEFAULT_TIME_EARLY } from './constants';

export const useTimeEarlyDynamicFields = (timeEarly = DEFAULT_TIME_EARLY) => {
  const { t } = useTranslation('messages');

  const datetimeEarlyKey = formatDynamicFieldKey(DynamicFieldKey_Enum.DATETIME_FORMAL_EARLY);
  const timeEarlyKey = formatDynamicFieldKey(DynamicFieldKey_Enum.TIME_EARLY);
  const timesEarlyKey = formatDynamicFieldKey(DynamicFieldKey_Enum.TIMES_EARLY);
  const firstDateTimeEarlyKey = formatDynamicFieldKey(DynamicFieldKey_Enum.DATETIME_FORMAL_FIRST_EARLY);
  const firstTimeEarlyKey = formatDynamicFieldKey(DynamicFieldKey_Enum.FIRST_TIME_EARLY);

  const timeEarlyValue = timeEarly.replace('m', '');

  // {{DATETIME_FORMAL_EARLY}}}
  const datetimeEarlyLabelMap = {
    '15_DATETIME_EARLY': {
      label: t('15 Minutes Early w/ Date'),
      symbol: datetimeEarlyKey,
      val: t('Friday, October 2nd at 9:45 AM'),
    },
    '30_DATETIME_EARLY': {
      label: t('30 Minutes Early w/ Date'),
      symbol: datetimeEarlyKey,
      val: t('Friday, October 2nd at 9:30 AM'),
    },
    '45_DATETIME_EARLY': {
      label: t('45 Minutes Early w/ Date'),
      symbol: datetimeEarlyKey,
      val: t('Friday, October 2nd at 9:15 AM'),
    },
    '60_DATETIME_EARLY': {
      label: t('60 Minutes Early w/ Date'),
      symbol: datetimeEarlyKey,
      val: t('Friday, October 2nd at 9:00 AM'),
    },
  };

  // {{TIME_EARLY}}}
  const timeEarlyLabelMap = {
    '15_TIME_EARLY': {
      label: t('15 Minutes Early'),
      symbol: timeEarlyKey,
      val: t('9:45 AM'),
    },
    '30_TIME_EARLY': {
      label: t('30 Minutes Early'),
      symbol: timeEarlyKey,
      val: t('9:30 AM'),
    },
    '45_TIME_EARLY': {
      label: t('45 Minutes Early'),
      symbol: timeEarlyKey,
      val: t('9:15 AM'),
    },
    '60_TIME_EARLY': {
      label: t('60 Minutes Early'),
      symbol: timeEarlyKey,
      val: t('9:00 AM'),
    },
  };

  // {{TIMES_EARLY}}}
  const timesEarlyLabelMap = {
    '15_TIMES_EARLY': {
      label: t('15 Minutes Early'),
      symbol: timesEarlyKey,
      val: t('9:45 AM'),
    },
    '30_TIMES_EARLY': {
      label: t('30 Minutes Early'),
      symbol: timesEarlyKey,
      val: t('9:30 AM'),
    },
    '45_TIMES_EARLY': {
      label: t('45 Minutes Early'),
      symbol: timesEarlyKey,
      val: t('9:15 AM'),
    },
    '60_TIMES_EARLY': {
      label: t('60 Minutes Early'),
      symbol: timesEarlyKey,
      val: t('9:00 AM'),
    },
  };

  // {{DATETIME_FORMAL_FIRST_EARLY}}}
  const firstDateTimeEarlyLabelMap = {
    '15_DATETIME_FORMAL_FIRST_EARLY': {
      label: t('15 Minutes Before First Date'),
      symbol: firstDateTimeEarlyKey,
      val: t('Friday, October 2nd at 9:45 AM'),
    },
    '30_DATETIME_FORMAL_FIRST_EARLY': {
      label: t('30 Minutes Before First Date'),
      symbol: firstDateTimeEarlyKey,
      val: t('Friday, October 2nd at 9:30 AM'),
    },
    '45_DATETIME_FORMAL_FIRST_EARLY': {
      label: t('45 Minutes Before First Date'),
      symbol: firstDateTimeEarlyKey,
      val: t('Friday, October 2nd at 9:15 AM'),
    },
    '60_DATETIME_FORMAL_FIRST_EARLY': {
      label: t('60 Minutes Before First Date'),
      symbol: firstDateTimeEarlyKey,
      val: t('Friday, October 2nd at 9:00 AM'),
    },
  };

  // {{FIRST_TIME_EARLY}}}
  const firstTimeEarlyLabelMap = {
    '15_FIRST_TIME_EARLY': {
      label: t('15 Minutes Before First Time'),
      symbol: firstTimeEarlyKey,
      val: t('9:45 AM'),
    },
    '30_FIRST_TIME_EARLY': {
      label: t('30 Minutes Before First Time'),
      symbol: firstTimeEarlyKey,
      val: t('9:30 AM'),
    },
    '45_FIRST_TIME_EARLY': {
      label: t('45 Minutes Before First Time'),
      symbol: firstTimeEarlyKey,
      val: t('9:15 AM'),
    },
    '60_FIRST_TIME_EARLY': {
      label: t('60 Minutes Before First Time'),
      symbol: firstTimeEarlyKey,
      val: t('9:00 AM'),
    },
  };

  const getDefaultTimeEarly = (obj: Record<string, DynamicFieldAttributes>) => {
    const values = Object.values(obj);
    return values.find((field) => field.label.includes(timeEarlyValue)) ?? values[0];
  };

  const defaultDynamicFieldLabelMap: Partial<Record<DynamicFieldKey_Enum, string>> = {
    [DynamicFieldKey_Enum.DATETIME_FORMAL_EARLY]: getDefaultTimeEarly(datetimeEarlyLabelMap).label,
    [DynamicFieldKey_Enum.TIME_EARLY]: getDefaultTimeEarly(timeEarlyLabelMap).label,
    [DynamicFieldKey_Enum.TIMES_EARLY]: getDefaultTimeEarly(timesEarlyLabelMap).label,
    [DynamicFieldKey_Enum.DATETIME_FORMAL_FIRST_EARLY]: getDefaultTimeEarly(firstDateTimeEarlyLabelMap).label,
    [DynamicFieldKey_Enum.FIRST_TIME_EARLY]: getDefaultTimeEarly(firstTimeEarlyLabelMap).label,
  };

  const dynamicFieldOptionsMap: Partial<Record<DynamicFieldKey_Enum, DynamicFieldAttributes[]>> = {
    [DynamicFieldKey_Enum.DATETIME_FORMAL_EARLY]: [
      datetimeEarlyLabelMap['15_DATETIME_EARLY'],
      datetimeEarlyLabelMap['30_DATETIME_EARLY'],
      datetimeEarlyLabelMap['45_DATETIME_EARLY'],
      datetimeEarlyLabelMap['60_DATETIME_EARLY'],
    ],
    [DynamicFieldKey_Enum.TIME_EARLY]: [
      timeEarlyLabelMap['15_TIME_EARLY'],
      timeEarlyLabelMap['30_TIME_EARLY'],
      timeEarlyLabelMap['45_TIME_EARLY'],
      timeEarlyLabelMap['60_TIME_EARLY'],
    ],
    [DynamicFieldKey_Enum.TIMES_EARLY]: [
      timesEarlyLabelMap['15_TIMES_EARLY'],
      timesEarlyLabelMap['30_TIMES_EARLY'],
      timesEarlyLabelMap['45_TIMES_EARLY'],
      timesEarlyLabelMap['60_TIMES_EARLY'],
    ],
    [DynamicFieldKey_Enum.DATETIME_FORMAL_FIRST_EARLY]: [
      firstDateTimeEarlyLabelMap['15_DATETIME_FORMAL_FIRST_EARLY'],
      firstDateTimeEarlyLabelMap['30_DATETIME_FORMAL_FIRST_EARLY'],
      firstDateTimeEarlyLabelMap['45_DATETIME_FORMAL_FIRST_EARLY'],
      firstDateTimeEarlyLabelMap['60_DATETIME_FORMAL_FIRST_EARLY'],
    ],
    [DynamicFieldKey_Enum.FIRST_TIME_EARLY]: [
      firstTimeEarlyLabelMap['15_FIRST_TIME_EARLY'],
      firstTimeEarlyLabelMap['30_FIRST_TIME_EARLY'],
      firstTimeEarlyLabelMap['45_FIRST_TIME_EARLY'],
      firstTimeEarlyLabelMap['60_FIRST_TIME_EARLY'],
    ],
  };

  const all15 = [
    timeEarlyLabelMap['15_TIME_EARLY'],
    datetimeEarlyLabelMap['15_DATETIME_EARLY'],
    timesEarlyLabelMap['15_TIMES_EARLY'],
    firstDateTimeEarlyLabelMap['15_DATETIME_FORMAL_FIRST_EARLY'],
    firstTimeEarlyLabelMap['15_FIRST_TIME_EARLY'],
  ];
  const all30 = [
    timeEarlyLabelMap['30_TIME_EARLY'],
    datetimeEarlyLabelMap['30_DATETIME_EARLY'],
    timesEarlyLabelMap['30_TIMES_EARLY'],
    firstDateTimeEarlyLabelMap['30_DATETIME_FORMAL_FIRST_EARLY'],
    firstTimeEarlyLabelMap['30_FIRST_TIME_EARLY'],
  ];
  const all45 = [
    timeEarlyLabelMap['45_TIME_EARLY'],
    datetimeEarlyLabelMap['45_DATETIME_EARLY'],
    timesEarlyLabelMap['45_TIMES_EARLY'],
    firstDateTimeEarlyLabelMap['45_DATETIME_FORMAL_FIRST_EARLY'],
    firstTimeEarlyLabelMap['45_FIRST_TIME_EARLY'],
  ];
  const all60 = [
    timeEarlyLabelMap['60_TIME_EARLY'],
    datetimeEarlyLabelMap['60_DATETIME_EARLY'],
    timesEarlyLabelMap['60_TIMES_EARLY'],
    firstDateTimeEarlyLabelMap['60_DATETIME_FORMAL_FIRST_EARLY'],
    firstTimeEarlyLabelMap['60_FIRST_TIME_EARLY'],
  ];

  const correspondingDynamicFields = {
    [timeEarlyLabelMap['15_TIME_EARLY'].label]: all15,
    [datetimeEarlyLabelMap['15_DATETIME_EARLY'].label]: all15,
    [timesEarlyLabelMap['15_TIMES_EARLY'].label]: all15,
    [firstDateTimeEarlyLabelMap['15_DATETIME_FORMAL_FIRST_EARLY'].label]: all15,
    [firstTimeEarlyLabelMap['15_FIRST_TIME_EARLY'].label]: all15,
    [timeEarlyLabelMap['30_TIME_EARLY'].label]: all30,
    [datetimeEarlyLabelMap['30_DATETIME_EARLY'].label]: all30,
    [timesEarlyLabelMap['30_TIMES_EARLY'].label]: all30,
    [firstDateTimeEarlyLabelMap['30_DATETIME_FORMAL_FIRST_EARLY'].label]: all30,
    [firstTimeEarlyLabelMap['30_FIRST_TIME_EARLY'].label]: all30,
    [timeEarlyLabelMap['45_TIME_EARLY'].label]: all45,
    [datetimeEarlyLabelMap['45_DATETIME_EARLY'].label]: all45,
    [timesEarlyLabelMap['45_TIMES_EARLY'].label]: all45,
    [firstDateTimeEarlyLabelMap['45_DATETIME_FORMAL_FIRST_EARLY'].label]: all45,
    [firstTimeEarlyLabelMap['45_FIRST_TIME_EARLY'].label]: all45,
    [timeEarlyLabelMap['60_TIME_EARLY'].label]: all60,
    [datetimeEarlyLabelMap['60_DATETIME_EARLY'].label]: all60,
    [timesEarlyLabelMap['60_TIMES_EARLY'].label]: all60,
    [firstDateTimeEarlyLabelMap['60_DATETIME_FORMAL_FIRST_EARLY'].label]: all60,
    [firstTimeEarlyLabelMap['60_FIRST_TIME_EARLY'].label]: all60,
  };

  const timeEarlyMap: Record<string, DynamicFieldAttributes[]> = {
    '15': [
      timeEarlyLabelMap['15_TIME_EARLY'],
      datetimeEarlyLabelMap['15_DATETIME_EARLY'],
      timesEarlyLabelMap['15_TIMES_EARLY'],
      firstDateTimeEarlyLabelMap['15_DATETIME_FORMAL_FIRST_EARLY'],
      firstTimeEarlyLabelMap['15_FIRST_TIME_EARLY'],
    ],
    '30': [
      timeEarlyLabelMap['30_TIME_EARLY'],
      datetimeEarlyLabelMap['30_DATETIME_EARLY'],
      timesEarlyLabelMap['30_TIMES_EARLY'],
      firstDateTimeEarlyLabelMap['30_DATETIME_FORMAL_FIRST_EARLY'],
      firstTimeEarlyLabelMap['30_FIRST_TIME_EARLY'],
    ],
    '45': [
      timeEarlyLabelMap['45_TIME_EARLY'],
      datetimeEarlyLabelMap['45_DATETIME_EARLY'],
      timesEarlyLabelMap['45_TIMES_EARLY'],
      firstDateTimeEarlyLabelMap['45_DATETIME_FORMAL_FIRST_EARLY'],
      firstTimeEarlyLabelMap['45_FIRST_TIME_EARLY'],
    ],
    '60': [
      timeEarlyLabelMap['60_TIME_EARLY'],
      datetimeEarlyLabelMap['60_DATETIME_EARLY'],
      timesEarlyLabelMap['60_TIMES_EARLY'],
      firstDateTimeEarlyLabelMap['60_DATETIME_FORMAL_FIRST_EARLY'],
      firstTimeEarlyLabelMap['60_FIRST_TIME_EARLY'],
    ],
  };

  return {
    correspondingDynamicFields,
    defaultDynamicFieldLabelMap,
    dynamicFieldOptionsMap,
    timeEarlyMap,
    timeEarlyValue,
  };
};
